import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Check, Pencil, Trash2, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * CountingMode — Tinder-style pantry counting flow. Show one item at a time
 * with three actions: Bekreft (no change), Endre mengde, Fjern.
 * Sorted oldest-verified first so users handle the riskiest items earliest.
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (b) => void
 *   items: PantryItem[]  — full pantry to iterate
 *   onChanged: () => void — called after each verify/update/delete so the
 *                            parent can refresh its data
 */
export default function CountingMode({ open, onOpenChange, items, onChanged }) {
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [editAmount, setEditAmount] = useState("");
  const [editing, setEditing] = useState(false);

  // Sort once when opening: oldest-verified (or never-verified) first.
  const queue = useMemo(() => {
    const rated = (items || []).map((it) => {
      const last = (it.last_verified_at || "").trim();
      // Items without a date sort to the top (priority 0 = never)
      const priority = last ? new Date(last + "T00:00:00Z").getTime() : 0;
      return { item: it, priority };
    });
    rated.sort((a, b) => a.priority - b.priority);
    return rated.map((r) => r.item);
  }, [items]);

  useEffect(() => {
    if (open) { setIdx(0); setEditing(false); setEditAmount(""); }
  }, [open]);

  const current = queue[idx];
  const total = queue.length;
  const done = idx >= total;

  // Helper to read the visible amount/unit from the first location-entry or legacy fields
  const firstEntry = current && (current.locations?.[0] || {
    amount: current.amount, unit: current.unit, expiry_date: current.expiry_date,
  });

  const next = () => setIdx((i) => i + 1);

  const handleVerify = async () => {
    if (!current || busy) return;
    setBusy(true);
    try {
      await api.verifyPantryItem(current.id);
      onChanged?.();
      next();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikkje bekrefte");
    } finally { setBusy(false); }
  };

  const handleSaveAmount = async () => {
    if (!current || busy) return;
    setBusy(true);
    try {
      // updatePantryItem mirrors first-entry amount into legacy + first location
      await api.updatePantryItem(current.id, { amount: editAmount });
      await api.verifyPantryItem(current.id);
      toast.success(`Oppdatert: ${current.name} → ${editAmount} ${firstEntry?.unit || ""}`);
      onChanged?.();
      setEditing(false);
      setEditAmount("");
      next();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikkje oppdatere");
    } finally { setBusy(false); }
  };

  const handleDelete = async () => {
    if (!current || busy) return;
    if (!window.confirm(`Fjern «${current.name}» fra beholdningen?`)) return;
    setBusy(true);
    try {
      await api.deletePantryItem(current.id);
      toast.success(`${current.name} fjernet`);
      onChanged?.();
      next();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikkje slette");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="counting-mode-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-stone-100">
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <Sparkles className="h-5 w-5 text-emerald-600" /> Opptellings-modus
          </DialogTitle>
          <DialogDescription className="text-xs">
            {done ? "Alle ferdig — bra jobba!" : `Vare ${Math.min(idx + 1, total)} av ${total} · eldste først`}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="px-6 py-10 text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-100 text-emerald-700 mb-3">
              <Check className="h-8 w-8" />
            </div>
            <h3 className="text-2xl font-semibold text-stone-800 mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Opptelling fullført</h3>
            <p className="text-sm text-stone-500 mb-4">Du har gått gjennom hele beholdningen. Alle endringer er lagret.</p>
            <button
              onClick={() => onOpenChange(false)}
              className="rounded-md bg-navy-800 hover:bg-navy-900 px-4 py-2 text-sm font-semibold text-white"
              data-testid="counting-close-btn"
            >
              Lukk
            </button>
          </div>
        ) : current ? (
          <div className="px-6 py-5">
            {/* Progress bar */}
            <div className="h-1.5 w-full bg-stone-100 rounded-full mb-4 overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(idx / total) * 100}%` }} />
            </div>

            {/* Item card */}
            <div className="bg-gradient-to-br from-stone-50 to-white border border-stone-200 rounded-xl p-5 mb-4 text-center" data-testid="counting-current-card">
              {current.image_url ? (
                <img src={current.image_url} alt="" className="w-20 h-20 mx-auto mb-2 rounded-lg object-cover bg-stone-100" />
              ) : null}
              <h4 className="text-xl font-semibold text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="counting-item-name">
                {current.name}
              </h4>
              <p className="text-sm text-stone-500 mt-1">
                {firstEntry?.amount || "?"} {firstEntry?.unit || "stk"}
                {firstEntry?.expiry_date && <span className="text-amber-700 ml-2">· Utløp {firstEntry.expiry_date}</span>}
              </p>
              {current.last_verified_at && (
                <p className="text-[11px] text-stone-400 mt-1">Sist sjekket: {current.last_verified_at}</p>
              )}
            </div>

            {editing ? (
              <div className="space-y-3" data-testid="counting-edit-section">
                <label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold block">
                  Ny mengde ({firstEntry?.unit || "stk"})
                </label>
                <input
                  type="text"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  placeholder={firstEntry?.amount || "0"}
                  className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-base focus:border-navy-800 outline-none"
                  data-testid="counting-edit-amount-input"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => { setEditing(false); setEditAmount(""); }}
                    disabled={busy}
                    className="flex-1 rounded-md border border-stone-200 px-3 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50"
                    data-testid="counting-edit-cancel"
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={handleSaveAmount}
                    disabled={busy || !editAmount.trim()}
                    className="flex-1 rounded-md bg-navy-800 hover:bg-navy-900 px-3 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                    data-testid="counting-edit-save"
                  >
                    {busy ? "Lagrer…" : "Lagre + neste"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={handleDelete}
                  disabled={busy}
                  className="flex flex-col items-center gap-1 rounded-lg border border-red-200 bg-white px-3 py-3 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
                  data-testid="counting-delete-btn"
                >
                  <Trash2 className="h-5 w-5" />
                  Fjern
                </button>
                <button
                  onClick={() => { setEditing(true); setEditAmount(firstEntry?.amount || ""); }}
                  disabled={busy}
                  className="flex flex-col items-center gap-1 rounded-lg border border-amber-200 bg-white px-3 py-3 text-xs font-semibold text-amber-700 hover:bg-amber-50 disabled:opacity-50 transition-colors"
                  data-testid="counting-edit-btn"
                >
                  <Pencil className="h-5 w-5" />
                  Endre
                </button>
                <button
                  onClick={handleVerify}
                  disabled={busy}
                  className="flex flex-col items-center gap-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 px-3 py-3 text-xs font-semibold text-white disabled:opacity-50 transition-colors"
                  data-testid="counting-verify-btn"
                >
                  <Check className="h-5 w-5" />
                  Bekreft
                </button>
              </div>
            )}

            <button
              onClick={() => { setIdx(total); }}
              className="w-full mt-3 text-[11px] text-stone-400 hover:text-stone-600 inline-flex items-center justify-center gap-1"
              data-testid="counting-skip-rest"
            >
              <X className="h-3 w-3" /> Hopp over resten
            </button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
